import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { userPool } from '../../auth/cognitoConfig';
import { Environment } from '../actions/environmentActions';
import { getKaminoTarget, isDetectivePath } from './kaminoTarget';
import { rememberMfaReturnTo } from '../../auth/mfaReturnTo';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface ApiFetchOptions {
  environment: Environment;
  method?: string;
  body?: any;
  /**
   * Pass-through to fetch's `keepalive` flag. Use for fire-and-forget logging
   * calls fired immediately before a `window.open` or navigation event — the
   * browser may otherwise cancel the in-flight request when focus shifts to
   * the new tab/page. Capped at 64 KB request body by the fetch spec.
   */
  keepalive?: boolean;
  /**
   * Background telemetry (the matching page's seen reports): an auth failure
   * only throws; it never sends the person to /login or /mfa mid-task.
   */
  quiet?: boolean;
}

/**
 * Retrieve the current Cognito session's ID token.
 * Returns null if no user is signed in or the session is invalid.
 */
function getIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

/**
 * Pull a readable message out of an error body.
 *
 * This API returns errors two ways: { error: { message } } and the plainer
 * { error: 'text' }. Reading only the first collapsed every plain-string
 * failure to a bare "Request failed" — caught 2026-09-10 when a specialist
 * finished a spoken Detective brief, the read timed out, and the screen said
 * "Request failed" while the server had said exactly what went wrong. It also
 * hid the Detective's "an investigation for this opportunity is already
 * running" 409.
 */

/**
 * Session monitor + email OTP (2026-09-15): the API answers 403 with a `code`
 * when the current login still has to pass the emailed code (MFA_REQUIRED) or
 * was revoked by an admin (SESSION_REVOKED). Route the browser accordingly and
 * throw so the caller stops. Returns false for any other response.
 */
function routeOnAuthGate(status: number, error: any): boolean {
  if (status !== 403) return false;
  const code = error?.error?.code || error?.code;
  if (code === 'MFA_REQUIRED') {
    if (!window.location.pathname.startsWith('/mfa')) {
      rememberMfaReturnTo(window.location.pathname + window.location.search + window.location.hash);
      window.location.href = '/mfa';
    }
    throw new Error('Verify the code we emailed you to continue.');
  }
  if (code === 'SESSION_REVOKED') {
    // AuthContext signs the stored session out once /api/auth/me confirms it; the next sign-in returns here.
    rememberMfaReturnTo(window.location.pathname + window.location.search + window.location.hash);
    window.location.href = '/login?revoked=1';
    throw new Error('This session was revoked. Please sign in again.');
  }
  return false;
}

function errorMessageFrom(error: any): string {
  return (
    error?.error?.message ||
    (typeof error?.error === 'string' ? error.error : null) ||
    error?.message ||
    'Request failed'
  );
}

/**
 * Build the Error thrown for a non-OK response, carrying the server's
 * machine-readable `code` (e.g. MISSING_INTERVIEW_RECORD → the off-Calendly
 * date/time dialog, window_closed → the WhatsApp "session expired" copy) and
 * the HTTP status. Callers test `err.code`; without this every typed 4xx
 * collapsed into a plain message and the dialogs never opened (2026-09-17).
 */
export interface ApiError extends Error {
  code?: string;
  status?: number;
  payload?: any;
}
function apiErrorFrom(status: number, error: any): ApiError {
  const err: ApiError = new Error(errorMessageFrom(error));
  const code = error?.error?.code || error?.code;
  if (typeof code === 'string' && code) err.code = code;
  err.status = status;
  err.payload = error;
  return err;
}

export async function apiFetch(path: string, options: ApiFetchOptions): Promise<any> {
  const { environment, method = 'GET', body, keepalive, quiet } = options;

  const token = await getIdToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Environment': environment,
  };
  // Which BUILD of the Detective service — a different axis from X-Environment
  // (which database). Attached here rather than at ~20 Detective call sites.
  if (isDetectivePath(path)) headers['X-Kamino-Target'] = getKaminoTarget();

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    keepalive,
  });

  // If the server returns 401, redirect to login
  if (response.status === 401 && !quiet) {
    window.location.href = '/login';
    throw new Error('Session expired. Redirecting to login.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    if (!quiet) routeOnAuthGate(response.status, error);
    throw apiErrorFrom(response.status, error);
  }

  // A quiet call's 204 has no body: success, not a JSON error (existing callers keep their behaviour).
  if (quiet && response.status === 204) return null;
  return response.json();
}

/**
 * POST that returns raw TEXT rather than JSON. Added 2026-09-10 for the
 * Detective's voice mode, whose WebRTC handshake exchanges SDP (a plain-text
 * format) — `apiFetch` parses JSON unconditionally and would destroy it.
 * Same auth and environment headers as everything else.
 */
export async function apiFetchText(
  path: string,
  options: { environment: Environment; body: string; contentType?: string; accept?: string },
): Promise<string> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    'Content-Type': options.contentType || 'application/json',
    Accept: options.accept || 'text/plain',
    'X-Environment': options.environment,
  };
  // Voice mode's SDP handshake is a Detective call too — without this, hanging
  // up would land on a different build than the one the brief was built on.
  if (isDetectivePath(path)) headers['X-Kamino-Target'] = getKaminoTarget();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${BASE_URL}${path}`, { method: 'POST', headers, body: options.body });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Session expired. Redirecting to login.');
  }
  const text = await response.text();
  if (!response.ok) {
    // The error path still answers JSON, so surface its message when there is one.
    let message = response.statusText;
    let parsed: any = null;
    try {
      const j = JSON.parse(text);
      parsed = j;
      routeOnAuthGate(response.status, j);
      message = j?.error?.message || j?.error || j?.message || message;
    } catch {
      if (text) message = text.slice(0, 300);
    }
    const err = apiErrorFrom(response.status, parsed || { message });
    err.message = message || 'Request failed';
    throw err;
  }
  return text;
}

export async function apiFetchBlob(
  path: string,
  options: { environment: Environment },
  filename: string,
): Promise<void> {
  const token = await getIdToken();

  const headers: Record<string, string> = {
    'X-Environment': options.environment,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { headers });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Session expired. Redirecting to login.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    routeOnAuthGate(response.status, error);
    throw apiErrorFrom(response.status, error);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

interface ApiFetchMultipartOptions {
  environment: Environment;
  method?: string;
  body: FormData;
  /** Lets the caller abort an in-flight upload (AbortController.signal). */
  signal?: AbortSignal;
}

export async function apiFetchMultipart(path: string, options: ApiFetchMultipartOptions): Promise<any> {
  const { environment, method = 'POST', body, signal } = options;

  const token = await getIdToken();

  const headers: Record<string, string> = {
    'X-Environment': environment,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body,
    signal,
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Session expired. Redirecting to login.');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    routeOnAuthGate(response.status, error);
    throw apiErrorFrom(response.status, error);
  }

  return response.json();
}
